<template>
  <div class="payment-thank-you">
    <div class="thank-you-container">
      <div class="success-animation">
        <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
          <circle class="checkmark__circle" cx="26" cy="26" r="25" fill="none"/>
          <path class="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
        </svg>
      </div>
      
      <h1 class="thank-you-title">Payment Successful!</h1>
      
      <div class="thank-you-content">
        <p class="thank-you-message">
          Thank you for subscribing to our service. Your payment has been processed successfully.
        </p>
        
        <div class="welcome-message">
          <h3>Welcome to Our Service!</h3>
          <p>Your account has been activated and you now have full access to all features.</p>
        </div>
        
        <div class="email-notification">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
            <polyline points="22,6 12,13 2,6"></polyline>
          </svg>
          <p>A confirmation email has been sent to your registered email address.</p>
        </div>
        
        <div class="loading-message" v-if="loading">
          <div class="spinner"></div>
          <p>Setting up your account...</p>
        </div>
        
        <div class="action-message" v-else>
          <div class="redirect-info">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="timer-icon">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <p>You will be automatically redirected to your dashboard in <strong>{{ countdown }}</strong> second{{ countdown !== 1 ? 's' : '' }}</p>
          </div>
          <p class="skip-message">Can't wait? <a href="#" @click.prevent="skipToLaunch">Click here to go now</a></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Userauth from "../auth/js/index";
import UserService from '../../services/userController';
import tagManager from '../../services/tagmanager';
import { revenueEvents } from '../plugins/posthog-events';

export default {
  name: 'SubscribeSuccess',
  data() {
    return {
      subscriptionInfo: null,
      userEmail: '',
      loading: true,
      countdown: 5,
      countdownTimer: null,
      navigationRetryTimer: null
    }
  },
  async mounted() {
    // GTM is now loaded globally in index.html

    // Also initialize Google Ads if needed
    // tagManager.callTagManager('google');

    try {
      let response = await UserService.isSubscribed({
        id: 'stripe',
        password: 'stripe'
      });  
      
      if(response.result){
        if(response.message.isactive){
          //Get Plan Details
          let planDetails = await UserService.getPlanInfo()

          // Track subscription started in PostHog
          revenueEvents.subscriptionStarted(
            planDetails.plan_name || 'subscription',
            planDetails.price || 0,
            'monthly'
          );


          // Store user data
          Userauth.localLogin(response.message);

          // Extract user info for display
          this.userEmail = response.message.email || '';

          // Calculate discounted price
          let finalPrice = 0;
          if (planDetails.message && planDetails.message.plan_price !== undefined) {
            const originalPrice = Number(planDetails.message.plan_price) || 0;
            const discountValue = Number(planDetails.message.plan_discount_value) || 0;
            const discountType = planDetails.message.plan_discount_type; // 'fixed' or 'percentage'

            if (discountType === 'percentage') {
              finalPrice = originalPrice - (originalPrice * (discountValue / 100));
            } else {
              // fixed discount
              finalPrice = originalPrice - discountValue;
            }
            finalPrice = Math.max(0, finalPrice); // Ensure non-negative
            finalPrice = Number(finalPrice.toFixed(2)); // Round to 2 decimal places
          }

          // Extract subscription info if available
          if (response.message.subscription) {
            this.subscriptionInfo = response.message.subscription;
          } else {
            // Use basic info if subscription details not available
            this.subscriptionInfo = {
              planId: planDetails.message?.plan_id,
              planName: planDetails.message?.plan_name || 'Subscription Plan',
              planPrice: finalPrice
            };
          }
          
          // Track successful payment conversion
          this.trackPaymentSuccess();
          
          // Set welcome modal flag for dashboard
          // Userauth.setWelcomeModalFlag(true);
          // Show success toast with navigation info
          // this.$toasted.show('Payment processed successfully! Redirecting to dashboard...', {
          //   theme: 'outline',
          //   position: "bottom-center",
          //   type: 'success',
          //   duration: 5000
          // });
          
          // Show navigation notification
          setTimeout(() => {
            // this.$toasted.show(`You will be redirected to your dashboard in a few seconds`, {
            //   theme: 'outline',
            //   position: "top-center",
            //   type: 'info',
            //   duration: 4000,
            //   icon: 'timer'
            // });
          }, 1000);
          
          // Set loading to false and start countdown
          this.loading = false;
          this.startCountdown();
          
          // Set up authentication refresh
          this.auth = setInterval(() => {
            this.Auth();
          }, 120000);
        }
        else{
          // Account not active - redirect to verification
          this.$toasted.show('Please verify your account to continue', {
            theme: 'outline',
            position: "bottom-center",
            type: 'info',
            duration: 3000
          });
          setTimeout(() => {
            this.$router.replace("/auth/verification");
          }, 2000);
        }
      }
      else{
        this.$toasted.show(response.message || 'Error processing subscription', {
          theme: 'outline',
          position: "bottom-center",
          type: 'error',
          duration: 3000
        });
        this.loading = false;
      }
    } catch (e) {
      console.error("Error fetching subscription details:", e);
      this.$toasted.show('Error loading subscription details', {
        theme: 'outline',
        position: "bottom-center",
        type: 'error',
        duration: 3000
      });
      this.loading = false;
    }
  },
  
  methods: {
    trackPaymentSuccess() {
      // Check if dataLayer exists (GTM loaded)
      if (!window.dataLayer) {
        console.error('❌ GTM Error: dataLayer not found. GTM may not be properly initialized.');
        
        return;
      }
      
      const transactionId = this.getTransactionId();
      // Since subscription details aren't available yet, we'll use placeholder values
      // These should be updated when actual data becomes available
      const planName = (this.subscriptionInfo && this.subscriptionInfo.planName) ? this.subscriptionInfo.planName : 'Subscription Plan';
      
      // Clear any previous ecommerce data
    
      
      // Push purchase event for GTM (simplified without unavailable data)

      let planValue = this.subscriptionInfo?.planPrice || 0
      let planId = this.subscriptionInfo.planId
      window.dataLayer.push({ ecommerce: null });
      let orderEvent =  {
          'event':'orderCompleted',
          'value': planValue,
          'transaction_id': transactionId,
          'currency': 'USD',
          'user_data': {
            "email": this.userEmail , /* Change yourEmailVariable to the actual Javascript variable name where you are storing the user’s email data. Do the same for the other variables below. Make sure the values aren't hashed. */  
          },
          item_id: planId,
          item_name: planName
      }
      
      // Push the purchase event (GTM will handle all conversions from this single event)
      
      window.dataLayer.push(orderEvent);
      
      console.log('✅ GTM Purchase Event Sent:', {
        transaction_id: transactionId,
        plan: planName,
        planId: planId,
        value: planValue
      });

      // Log to console for debugging
      console.group('📊 GTM Events Summary');
      console.log('Event: purchase');
      console.log('Transaction ID:', transactionId);
      console.log('Plan:', planName);
      console.log('Value:', planValue);
      console.log('User Email:', this.userEmail || 'Not provided');
      console.log('DataLayer Size:', window.dataLayer.length);
      console.groupEnd();
      
      // Show success notification
      this.$toasted.show(`✅ Subscription activated successfully!`, {
        theme: 'outline',
        position: "top-right",
        type: 'success',
        duration: 4000
      });
    },
    
    getTransactionId() {
  
      // Generate unique ID with timestamp and email
      const timestamp = Date.now();
      const emailPrefix = this.userEmail ? this.userEmail.split('@')[0] : 'user';
      return `TXN_${emailPrefix}_${timestamp}`;
    },
    
    startCountdown() {
      console.log('Starting countdown:', this.countdown);
      this.countdownTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer);
          this.navigateToDashboard();
        }
      }, 1000);
    },
    
    skipToLaunch() {
      // Clear countdown timer if running
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }
      this.navigateToDashboard();
    },
    
    navigateToDashboard() {
      // Show initial navigation message
      this.$toasted.show('🚀 Launching your dashboard...', {
        theme: 'outline',
        position: "bottom-center",
        type: 'success',
        duration: 2000
      });
      
      // Navigate to dashboard
      setTimeout(() => {
        this.$router.replace("/app/dashboard").catch(() => {
          console.log('Navigation to dashboard failed, will retry...');
        });
      }, 500);
      
      // Set up retry every 10 seconds if still on success page
      this.navigationRetryTimer = setInterval(() => {
        if (this.$route.path.includes('success')) {
          this.$router.replace("/app/dashboard").catch(() => {
            this.$toasted.show('Still having connection issues. Retrying...', {
              theme: 'outline',
              position: "top-center",
              type: 'warning',
              duration: 3000
            });
          });
        } else {
          // Successfully navigated, clear the timer
          clearInterval(this.navigationRetryTimer);
        }
      }, 10000);
    },
    
    Auth() {
      // Authentication refresh logic
      UserService.isAuthenticated().then((response) => {
        if (!response.result) {
          Userauth.Logout();
        }
      });
    }
  },
  
  beforeDestroy() {
    if (this.auth) {
      clearInterval(this.auth);
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
    if (this.navigationRetryTimer) {
      clearInterval(this.navigationRetryTimer);
    }
  }
}
</script>

<style scoped>
.payment-thank-you {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.thank-you-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 100%;
  padding: 40px;
  text-align: center;
}

/* Success Animation */
.success-animation {
  margin-bottom: 30px;
}

.checkmark {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: block;
  stroke-width: 2;
  stroke: #4bb543;
  stroke-miterlimit: 10;
  margin: 0 auto;
  position: relative;
  background: #4bb543;
  animation: scale .3s ease-in-out .9s both;
}

.checkmark__circle {
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  stroke-width: 2;
  stroke-miterlimit: 10;
  stroke: #4bb543;
  fill: #4bb543;
  animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.checkmark__check {
  transform-origin: 50% 50%;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  stroke: white;
  stroke-width: 3;
  animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
}

@keyframes stroke {
  100% {
    stroke-dashoffset: 0;
  }
}

@keyframes scale {
  0%, 100% {
    transform: none;
  }
  50% {
    transform: scale3d(1.1, 1.1, 1);
  }
}

@keyframes fill {
  100% {
    box-shadow: inset 0px 0px 0px 30px #4bb543;
  }
}

/* Content Styles */
.thank-you-title {
  color: #2c3e50;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 20px;
}

.thank-you-message {
  color: #6c757d;
  font-size: 18px;
  margin-bottom: 30px;
  line-height: 1.6;
}

.welcome-message {
  background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%);
  border: 1px solid #667eea30;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;
  text-align: center;
}

.welcome-message h3 {
  color: #667eea;
  font-size: 22px;
  margin-bottom: 15px;
  font-weight: 600;
}

.welcome-message p {
  color: #495057;
  font-size: 16px;
  margin: 0;
  line-height: 1.5;
}

.email-notification {
  display: flex;
  align-items: center;
  gap: 15px;
  background: #e7f5ff;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 30px;
}

.email-notification svg {
  flex-shrink: 0;
  color: #0095ff;
}

.email-notification p {
  margin: 0;
  color: #495057;
  text-align: left;
  font-size: 14px;
}

.loading-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  padding: 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-message p {
  color: #495057;
  font-size: 16px;
  margin: 0;
}

.action-message {
  padding: 20px;
}

.redirect-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 15px;
}

.timer-icon {
  color: #667eea;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.redirect-info p {
  color: #495057;
  font-size: 16px;
  margin: 0;
}

.redirect-info strong {
  color: #667eea;
  font-weight: 700;
  font-size: 20px;
}

.skip-message {
  color: #6c757d;
  font-size: 14px;
  margin: 0;
}

.skip-message a {
  color: #667eea;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
}

.skip-message a:hover {
  color: #764ba2;
  text-decoration: underline;
}

/* Responsive Design */
@media (max-width: 768px) {
  .thank-you-container {
    padding: 30px 20px;
  }
  
  .thank-you-title {
    font-size: 26px;
  }
  
  .thank-you-message {
    font-size: 16px;
  }
  
  .email-notification {
    flex-direction: column;
    text-align: center;
  }
  
  .email-notification p {
    text-align: center;
  }
}
</style>
